// Lightweight consent store for TheCityPair.
//
// Categories follow Google Consent Mode v2 conventions so this maps 1:1
// onto GA4 / GTM `gtag('consent', 'update', { ... })` when those are
// wired in. Persisted to localStorage under a versioned key so we can
// re-prompt users if the schema ever changes.

import { useEffect, useState } from "react";

const STORAGE_KEY = "tcp_cookie_consent_v1";
const CHANGE_EVENT = "tcp:consent-change";
const OPEN_EVENT = "tcp:open-cookie-settings";

export const CONSENT_VERSION = 1;

export const DEFAULT_CONSENT = {
  v: CONSENT_VERSION,
  ts: null,
  necessary: true, // always on, not user-configurable
  analytics: false,
  marketing: false,
};

export function getConsent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== CONSENT_VERSION) return null;
    return { ...DEFAULT_CONSENT, ...parsed };
  } catch (e) {
    // localStorage disabled (private mode / blocked by CSP / corrupted JSON)
    // → consent is effectively undecided. Log in dev only.
    if (process.env.NODE_ENV !== "production") console.debug("getConsent:", e);
    return null;
  }
}

export function hasDecided() {
  return getConsent() !== null;
}

export function setConsent(partial) {
  const next = {
    ...DEFAULT_CONSENT,
    ...(getConsent() || {}),
    ...partial,
    v: CONSENT_VERSION,
    ts: new Date().toISOString(),
    necessary: true,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (e) {
    // Storage disabled in user's browser; preference can't persist this
    // session. Don't block the consent flow — log in dev only.
    if (process.env.NODE_ENV !== "production") console.debug("setConsent:", e);
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }));
  }
  return next;
}

export function acceptAll() {
  return setConsent({ analytics: true, marketing: true });
}

export function rejectAll() {
  return setConsent({ analytics: false, marketing: false });
}

export function openCookieSettings() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(OPEN_EVENT));
  }
}

export function onCookieSettingsOpen(handler) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(OPEN_EVENT, handler);
  return () => window.removeEventListener(OPEN_EVENT, handler);
}

export function useConsent() {
  const [consent, setLocalConsent] = useState(() => getConsent());

  useEffect(() => {
    const handler = (e) => setLocalConsent(e.detail || getConsent());
    window.addEventListener(CHANGE_EVENT, handler);
    return () => window.removeEventListener(CHANGE_EVENT, handler);
  }, []);

  return consent;
}
